import {
  columnToFieldKey,
  entityTypeNames,
  type DraftChangelogUpdate,
  type EntityRegistry,
  type EntityTable,
  type FieldConfigShort,
  type EntityType,
  type RowDiff,
} from "@renewmap/model";
import { hasValueChanged } from "../../common/table/cell-renderers";
import { Point } from "geojson";

const ignoredColumns = [
  "updated",
  "created",
  "id",
  "to_be_published",
  "published",
  "information",
  "author",
  "logo_flag",
];

const ignoredOperationTypes = ["delete", "lead", "other"];

const ignoredFieldNames = ["Organisations", "Project Group", "Updated"];

// Mirrors pages/client/src/map/utils/zoom-level.ts's getProjectZoomLevel
const getProjectZoomLevel = (technology: string | null) => {
  switch (technology) {
    case "Battery":
    case "Coal (Black)":
    case "Coal (Brown)":
    case "Data Centre":
    case "Distillate":
    case "Gas":
    case "Nuclear":
    case "Biogas":
    case "Biomass":
      return 16; // building-level zoom
    case "Solar PV":
      return 12;
  }
  return 10; // default zoom
};

const getFieldUpdatedMessage = (
  entityType: EntityType | null,
  displayName: string | undefined,
  fieldKey: string | undefined,
  oldValue: unknown,
  newValue: unknown,
) => {
  const entityTypeName = entityType
    ? (entityTypeNames[entityType] ?? entityType)
    : "";

  if (fieldKey?.startsWith("geom_area")) {
    return `Updated ${entityTypeName} boundary`;
  }

  switch (fieldKey) {
    case "epbc_status_prj":
    case "epbc_status_net":
    case "epbc_status_sub":
      if (oldValue == null && newValue === "Referral Lodged") {
        return "EPBC referral published";
      }
      if (oldValue === "Referral Lodged" && newValue === "Approved") {
        return "EPBC referral decision - Not Controlled Action";
      }
      if (oldValue === "Referral Lodged" && newValue === "Assessment") {
        return "EPBC referral decision - Controlled Action";
      }
      if (oldValue === "Assessment" && newValue === "Approved") {
        return "EPBC decision published - Approved";
      }
      break;
    case "epbc_referral_status_prj":
    case "epbc_referral_status_net":
    case "epbc_referral_status_sub":
    case "epbc_assessment_status_prj":
    case "epbc_assessment_status_net":
    case "epbc_assessment_status_sub":
      if (newValue != null) {
        return `Updated EPBC status - ${newValue}`;
      }
      break;
    case "nsw_ssp_prj":
      if (newValue != null) {
        return `Updated NSW State Significant Project status - ${newValue}`;
      }
      break;
    case "project_status_prj":
    case "infrastructure_status_net":
    case "infrastructure_status_gas":
    case "substation_status_sub":
      return `Revised ${entityTypeName} status - ${oldValue} -> ${newValue}`;
    case "nrepl_prj":
      if (oldValue == null && newValue != null) {
        return "National Renewable Energy Priority List";
      }
      break;
  }

  if (fieldKey?.startsWith("da_status_")) {
    return `Revised approval status - ${oldValue} -> ${newValue}`;
  }

  if (displayName) {
    return `Updated ${displayName}`;
  }
  return `Updated ${entityTypeName}`;
};

const getEntityAddedMessage = (
  entityType: EntityType | null,
  country: string | null,
) => {
  const entityTypeName = entityType
    ? (entityTypeNames[entityType] ?? entityType)
    : "entity";
  const suffix = country === "New Zealand" ? " (NZ)" : "";
  return `New ${entityTypeName} added to RenewMap${suffix}`;
};

// Get update type from field_key, if applicable. Otherwise undefined.
const epbcFieldKeys = new Set([
  "epbc_code_net",
  "epbc_status_net",
  "epbc_code_sub",
  "epbc_code_ass",
  "epbc_status_ass",
  "epbc_status_sub",
  "epbc_code_prj",
  "epbc_status_prj",
]);

const parseUpdateTypeFromChangedField = (
  fieldKey: string,
): DraftChangelogUpdate["update_type"] | undefined => {
  if (fieldKey.startsWith("da_")) {
    return "DA";
  } else if (epbcFieldKeys.has(fieldKey)) {
    return "EPBC";
  } else if (
    new Set([
      "project_status_prj",
      "substation_status_sub",
      "infrastructure_status_net",
      "infrastructure_status_gas",
    ]).has(fieldKey)
  ) {
    return "STATUS";
  }
  return undefined;
};

// Get update type from linked entity, if applicable. Otherwise undefined
const parseUpdateTypeFromLinkedEntity = (
  linkedId: string,
  entityRegistry: EntityRegistry,
) => {
  if (entityRegistry[linkedId]?.entity_type === "organisation") {
    return "ORGANISATION";
  }
  return undefined;
};

const movePointSlightly = (geojson: Point): Point => {
  // adjust the lat lon to avoid overlapping with the project point
  const [lng, lat] = geojson.coordinates;

  // Approximate conversion: 0.005-0.008 degrees is roughly 500-800 meters
  const latOffset = Math.random() * 0.003 + 0.005;
  const lngOffset = Math.random() * 0.003 + 0.005;

  // Randomly decide direction (positive or negative)
  const newLat = lat + (Math.random() < 0.5 ? latOffset : -latOffset);
  const newLng = lng + (Math.random() < 0.5 ? lngOffset : -lngOffset);

  return {
    type: "Point",
    coordinates: [newLng, newLat],
  };
};

const diffArray = <T>(
  draft: T[] | null,
  publicValue: T[] | null,
): { added: T[]; removed: T[] } => {
  const draftSet = new Set(draft ?? []);
  const publicSet = new Set(publicValue ?? []);

  return {
    added: [...draftSet.difference(publicSet)],
    removed: [...publicSet.difference(draftSet)],
  };
};

// Get changelogs from diffs on entity tables
export const calculateEntityTableUpdates = (
  table: EntityTable,
  changes: RowDiff[],
  fieldConfigs: FieldConfigShort[],
  entityRegistry: EntityRegistry,
): DraftChangelogUpdate[] => {
  const result: DraftChangelogUpdate[] = [];

  if (!changes.length) {
    return result;
  }

  const fieldConfigByKey = new Map(
    fieldConfigs.map((config) => [config.field_key, config]),
  );

  for (const change of changes) {
    if (ignoredOperationTypes.includes(change.operation_type)) {
      continue;
    }
    const entityItem = entityRegistry[change.id];
    const entity_name = entityItem?.entity_name ?? "Unknown";
    const entity_type = (entityItem?.entity_type ?? null) as EntityType | null;
    const country = (change.values["country"]?.draft as string | null) ?? null;
    let entity_point = entityItem?.point ?? null;
    const entity_zoom = getProjectZoomLevel(entityItem?.technology ?? null);

    // inserts on entity tables are treated as "New" changelogs, with no field-level changes
    if (change.operation_type === "insert") {
      result.push({
        entity_name,
        timestamp: new Date(),
        entity_id: change.id,
        entity_type,
        update_type: "NEW",
        field_name: null,
        field_key: null,
        title: getEntityAddedMessage(entity_type, country),
        description: entity_name,
        link_text: null,
        link_url: null,
        group_name: null,
        group_rank: null,
        zoom: entity_zoom,
        old_value: null,
        new_value: null,
        point: entity_point,
        linked_entity_id: null,
      });
      continue;
    }

    for (const column of Object.keys(change.values)) {
      if (ignoredColumns.includes(column)) {
        continue;
      }

      if (hasValueChanged(column, change.values[column]) === "nochange") {
        continue;
      }

      let fieldKey: string | undefined = columnToFieldKey[table]?.[column];
      // special case: gas infrastructure uses the same table as electricity, but the field keys are different
      if (fieldKey && entity_type === "gas") {
        fieldKey = fieldKey.replace(/_net$/, "_gas");
      }

      const displayName = fieldKey
        ? fieldConfigByKey.get(fieldKey)?.field_name
        : undefined;

      const specialUpdateType = parseUpdateTypeFromChangedField(fieldKey ?? "");
      if (entity_point && specialUpdateType) {
        entity_point = movePointSlightly(entity_point);
      }

      const columnValue = change.values[column];
      result.push({
        entity_name,
        timestamp: new Date(),
        entity_id: change.id,
        entity_type,
        update_type: specialUpdateType ?? "REVISION",
        field_name: column,
        field_key: fieldKey ?? null,
        title: getFieldUpdatedMessage(
          entity_type,
          displayName,
          fieldKey ?? column,
          columnValue.public,
          columnValue.draft,
        ),
        description: entity_name,
        link_text: null,
        link_url: null,
        group_name: null,
        group_rank: null,
        zoom: entity_zoom,
        old_value: columnValue.public,
        new_value: columnValue.draft,
        point: entity_point,
        linked_entity_id: null,
      });
    }
  }

  return result;
};

// get changleogs from diffs on field_values_v1
export const calculateFieldValueUpdates = (
  changes: RowDiff[],
  fieldConfigs: FieldConfigShort[],
  entityRegistry: EntityRegistry,
  newEntityIds: Set<string>,
): DraftChangelogUpdate[] => {
  const result: DraftChangelogUpdate[] = [];

  if (!changes.length) {
    return result;
  }

  for (const change of changes) {
    if (ignoredOperationTypes.includes(change.operation_type)) {
      continue;
    }
    const field_name = change.values["field_name"]?.draft as string;
    if (ignoredFieldNames.includes(field_name)) {
      continue;
    }
    // Skip if the entity_id is a new entity (single "New" row will be generated for it)
    const entity_id = change.values["entity_id"]?.draft as string;
    if (newEntityIds.has(entity_id)) {
      continue;
    }
    const entity_type = change.values["entity_type"]?.draft as EntityType;

    const entityItem = entityRegistry[entity_id];
    const entity_name = entityItem?.entity_name ?? "Unknown";
    let entity_point = entityItem?.point ?? null;
    const entity_zoom = getProjectZoomLevel(entityItem?.technology ?? null);

    const fieldConfig = fieldConfigs.find(
      (definition) =>
        definition.entity_type === entity_type &&
        definition.field_name === field_name,
    );
    if (!fieldConfig) {
      throw new Error(
        `No field config found for entity_type "${entity_type}" field_name "${field_name}"`,
      );
    }
    const fieldType = fieldConfig.field_type;

    const specialUpdateType = parseUpdateTypeFromChangedField(
      fieldConfig.field_key,
    );

    // Handle linked entity changes
    if (fieldType === "entity_id_array") {
      const arrayDiff = change.values["value_string_array"];
      const { added, removed } = diffArray(
        (arrayDiff?.draft as string[] | null) ?? null,
        (arrayDiff?.public as string[] | null) ?? null,
      );
      if (!added.length && !removed.length) {
        continue;
      }

      const linkedChanges = [
        ...added.map((linkedId) => ({ linkedId, isAdded: true })),
        ...removed.map((linkedId) => ({ linkedId, isAdded: false })),
      ];

      for (const { linkedId, isAdded } of linkedChanges) {
        const entityUpdateType = parseUpdateTypeFromLinkedEntity(
          linkedId,
          entityRegistry,
        );
        const updateType = specialUpdateType ?? entityUpdateType ?? "REVISION";
        if (entity_point && updateType !== "REVISION") {
          entity_point = movePointSlightly(entity_point);
        }

        result.push({
          entity_name,
          timestamp: new Date(),
          entity_id,
          entity_type,
          update_type: updateType,
          field_name,
          field_key: fieldConfig.field_key,
          title: `${field_name} ${isAdded ? "added" : "removed"}`,
          description: entity_name,
          link_text: null,
          link_url: null,
          group_name: null,
          group_rank: null,
          zoom: entity_zoom,
          old_value: isAdded ? null : linkedId,
          new_value: isAdded ? linkedId : null,
          point: entity_point,
          linked_entity_id: linkedId,
        });
      }
      continue;
    }
    // Handle other field_values_v1 changes
    else {
      const valueColumns = [
        "value_string",
        "value_number",
        "value_date",
        "value_array",
        "value_string_array",
      ];
      const changedValueColumn = valueColumns.find(
        (column) =>
          hasValueChanged(column, change.values[column]) !== "nochange",
      );
      if (!changedValueColumn) {
        continue;
      }
      const fieldValue = change.values[changedValueColumn];
      const updateType = specialUpdateType ?? "REVISION";
      if (entity_point && updateType !== "REVISION") {
        entity_point = movePointSlightly(entity_point);
      }

      result.push({
        entity_name,
        timestamp: new Date(),
        entity_id,
        entity_type,
        update_type: updateType,
        field_name,
        field_key: fieldConfig.field_key,
        title: getFieldUpdatedMessage(
          entity_type,
          field_name,
          fieldConfig.field_key,
          fieldValue?.public ?? null,
          fieldValue?.draft ?? null,
        ),
        description: entity_name,
        link_text: null,
        link_url: null,
        group_name: null,
        group_rank: null,
        zoom: entity_zoom,
        old_value: fieldValue?.public ?? null,
        new_value: fieldValue?.draft ?? null,
        point: entity_point,
        linked_entity_id: null,
      });
    }
  }

  return result;
};
